/**
 * Hook that encapsulates Matches page state, data sync, and event handlers.
 */

import { useAutoChatCreation } from "@features/messaging/hooks/useAutoChatCreation";
import { useConcurrentMatchHandling } from "@features/messaging/hooks/useConcurrentMatchHandling";
import { useMatchesRealtime } from "@features/messaging/hooks/useMatchesRealtime";
import { useAllMatches, useCreateChat, useMarkMatchAsRead, useNewMatches } from "@features/messaging/queries";
import type { Match } from "@features/messaging/types";
import logger from "@logger";
import { chatsQueryKeys, matchesQueryKeys } from "@shared/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getChatIdFromResult,
  getMatchesErrorMessage,
  getMatchKey,
  isRapidClick,
  RAPID_CLICK_WINDOW_MS,
} from "../utils/matchesPageUtils";

export interface UseMatchesPageReturn {
  newMatches: Match[];
  allMatches: Match[];
  isLoading: boolean;
  isNewMatchesLoading: boolean;
  isAllMatchesLoading: boolean;
  /** Blocking errors (queries / chat creation) — full-page takeover when set. */
  error: string | null;
  /** Non-blocking: snapshot listener died; list data may still be valid. */
  realtimeError: string | null;
  handleNewMatchClick: (match: Match) => Promise<void>;
  handleChatClick: (match: Match) => void;
  handleProfileClick: (userId: string) => void;
  handleRetry: () => void;
  retryRealtime: () => void;
}

export function useMatchesPage(currentUserId: string): UseMatchesPageReturn {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  const { data: newMatchesData, isLoading: newMatchesLoading, error: newMatchesError } = useNewMatches(currentUserId);
  const { data: allMatchesData, isLoading: allMatchesLoading, error: allMatchesError } = useAllMatches(currentUserId);

  const createChatMutation = useCreateChat({
    onSuccess: (_chatId, variables) => {
      logger.info("Chat created successfully", { chatId: _chatId, variables });
    },
    onError: (err) => {
      logger.error("Error creating chat:", err);
    },
  });

  const { error: realtimeError, retry: retryRealtime } = useMatchesRealtime(currentUserId);
  const { createChatError } = useAutoChatCreation(currentUserId);
  const markMatchAsReadMutation = useMarkMatchAsRead();
  const { handleConcurrentLikes, handleRapidLikes, handleNetworkFailure, handleDuplicateMatch } =
    useConcurrentMatchHandling();

  const lastClickTime = useRef<Map<string, number>>(new Map());

  const newMatches = newMatchesData ?? [];
  const allMatches = allMatchesData ?? [];
  const hasBlockingError = !!(
    newMatchesError ??
    allMatchesError ??
    createChatMutation.error ??
    createChatError ??
    recoveryError
  );
  const error = hasBlockingError
    ? getMatchesErrorMessage({
        newMatchesError: newMatchesError ?? null,
        allMatchesError: allMatchesError ?? null,
        createChatError: createChatMutation.error ?? createChatError ?? null,
      }) ||
      recoveryError ||
      "Failed to load matches"
    : null;

  const attemptNetworkRecovery = async (matchId: string) => {
    try {
      const recoveryResult = await handleNetworkFailure(currentUserId, matchId);
      const chatId = getChatIdFromResult(recoveryResult);
      if (chatId) {
        logger.info("Recovered from network failure", { matchId, chatId });
        setRecoveryError(null);
      } else {
        setRecoveryError("Failed to create match. Please try again.");
      }
    } catch (recoveryErr) {
      logger.error("Network failure recovery also failed:", recoveryErr);
      setRecoveryError("Failed to create match. Please try again.");
    }
  };

  const handleRapidClick = async (matchId: string): Promise<boolean> => {
    const matchKey = getMatchKey(currentUserId, matchId);
    const now = Date.now();
    const lastClick = lastClickTime.current.get(matchKey);

    if (isRapidClick(lastClick, now, RAPID_CLICK_WINDOW_MS)) {
      logger.warn("Rapid click detected, using rapid likes handler", { matchId });
      const result = await handleRapidLikes(currentUserId, matchId);
      const chatId = getChatIdFromResult(result);
      if (chatId) {
        logger.info("Match created via rapid likes handler", { matchId, chatId });
      }
      return true;
    }

    lastClickTime.current.set(matchKey, now);
    return false;
  };

  const navigateToChat = (chatId: string, match: Match) => {
    navigate(`/chat/${chatId}`, {
      state: {
        otherUser: {
          uid: match.user.uid,
          display_name: match.user.display_name,
          photo_url: match.user.photo_url,
        },
      },
    });
  };

  const invalidateMatchQueries = () => {
    queryClient.invalidateQueries({ queryKey: matchesQueryKeys.allMatches(currentUserId) });
    queryClient.invalidateQueries({ queryKey: chatsQueryKeys.user(currentUserId) });
  };

  const resolveOrCreateChat = async (match: Match): Promise<string | null> => {
    const isRapid = await handleRapidClick(match.user.uid);
    if (isRapid) {
      return null;
    }

    const duplicateResult = await handleDuplicateMatch(currentUserId, match.user.uid);
    const duplicateChatId =
      duplicateResult.isDuplicate && "chatId" in duplicateResult ? getChatIdFromResult(duplicateResult) : undefined;
    if (duplicateChatId) {
      logger.info("Duplicate match detected, chat already exists", {
        matchId: match.user.uid,
        chatId: duplicateChatId,
      });
      invalidateMatchQueries();
      return duplicateChatId;
    }

    const result = await handleConcurrentLikes(currentUserId, match.user.uid);
    const chatId = getChatIdFromResult(result);
    if (chatId) {
      logger.info("Match created successfully", { matchId: match.user.uid, chatId });
      invalidateMatchQueries();
      return chatId;
    }
    if (result.isDuplicate) {
      logger.info("Duplicate match request", { matchId: match.user.uid });
    } else if (result.error) {
      logger.error("Match creation failed", { matchId: match.user.uid, error: result.error });
      setRecoveryError(result.error);
    }
    return null;
  };

  const handleNewMatchClick = async (match: Match) => {
    logger.info("New match clicked", { matchId: match.user.uid });

    if (match.chat?.id) {
      navigateToChat(match.chat.id, match);
      return;
    }

    try {
      const chatId = await resolveOrCreateChat(match);
      if (chatId) {
        navigateToChat(chatId, match);
      }
    } catch (err) {
      logger.error("Error handling new match click:", err);
      await attemptNetworkRecovery(match.user.uid);
    }
  };

  const handleChatClick = (match: Match) => {
    const chatId = match.chat?.id;
    logger.info("Chat clicked", { matchId: match.user.uid, chatId });
    if (chatId) {
      markMatchAsReadMutation.mutate({ chatId, userId: currentUserId });
      navigate(`/chat/${chatId}`, {
        state: {
          otherUser: {
            uid: match.user.uid,
            display_name: match.user.display_name,
            photo_url: match.user.photo_url,
          },
        },
      });
    }
  };

  const handleProfileClick = (userId: string) => {
    navigate(`/match/${userId}`);
  };

  const handleRetry = () => {
    setRecoveryError(null);
    createChatMutation.reset();
    retryRealtime();
    queryClient.invalidateQueries({ queryKey: matchesQueryKeys.allMatches(currentUserId) });
  };

  return {
    newMatches,
    allMatches,
    isLoading: newMatchesLoading || allMatchesLoading,
    isNewMatchesLoading: newMatchesLoading,
    isAllMatchesLoading: allMatchesLoading,
    error: error || null,
    realtimeError,
    handleNewMatchClick,
    handleChatClick,
    handleProfileClick,
    handleRetry,
    retryRealtime,
  };
}
